/**
 * "ตอนนี้เครื่องมีงานค้างอยู่ไหม" — ใช้ตัดสินว่าจะโหลดหน้าใหม่ทับได้หรือยัง
 *
 * โหลดใหม่ทับตอนกำลังคิดเงิน = บิลหาย เงินไม่ตรง ร้านทะเลาะกับลูกค้าคาหน้าเคาน์เตอร์
 * ตัวนี้เลยเป็นด่านเดียวที่กั้นเรื่องนั้น ห้ามมีการเช็คชุดที่สองที่อื่น
 *
 * รวมของเดิมที่ ModalAppReload (รุ่นก่อน #62) เคยเช็คไว้ (อยู่หน้าตะกร้า / มีของค้างในตะกร้า /
 * ล็อคหน้าจออยู่) แล้วเติมอีกสามอย่างที่ของเดิมไม่ได้ดู:
 *   - จอคิดเงินเปิดอยู่ (paymentStore.active)
 *   - ยังมี job/ใบเสร็จค้างในคิว offline ที่ยังเขียนขึ้น cloud ไม่สำเร็จ
 *   - เน็ตหลุด — SPA ตัวนี้โหลดไฟล์จาก hosting ถ้าโหลดใหม่ตอนออฟไลน์คือจอขาว
 */

/** ชนิดของ payload ที่ plugin ฝั่ง pos ฝากไว้ใน localStorage ระหว่างรอเขียนขึ้น cloud */
const SYNC_QUEUE_TYPES = new Set([
  "job",
  "receipt",
  "voidItem",
  "openMenu",
  "changeProductPrice",
]);

/** คิว sync อยู่ใน localStorage ไม่ใช่ reactive state จึงต้องเดินไปดูเองเป็นระยะ */
const SYNC_QUEUE_POLL_MS = 5000;

export const useAppBusy = () => {
  const route = useRoute();
  const cartItemsStore = useCartItemsStore();
  const paymentStore = usePaymentStore();
  const employeePinStore = useEmployeePinStore();
  const onlineStatusStore = useOnlineStatusStore();
  const shopStore = useShopStore();

  const tick = ref(0);
  const timer = setInterval(() => {
    tick.value += 1;
  }, SYNC_QUEUE_POLL_MS);

  onScopeDispose(() => clearInterval(timer));

  const hasUnsavedCart = computed(() => cartItemsStore.items.length > 0);
  const isOnCartPage = computed(() => route.name === "cart");
  const isOnPayment = computed(() => paymentStore.active === true);
  // ปิดร้านเป็นหลายขั้นตอน (sync / พิมพ์รายงาน / ล้าง jobchain / ส่งอีเมล) โหลดทับกลางทางไม่ได้
  const isOnCloseShop = computed(() => route.path.startsWith("/dashboard/close"));
  const isOffline = computed(() => onlineStatusStore.isOnline === false);
  const hasQueuedSync = computed(() => {
    tick.value; // ผูกกับ tick เพื่อให้ computed ถูกคิดใหม่ทุกรอบ poll
    return hasQueuedSyncPayload(shopStore.storeId);
  });

  // ล็อคหน้าจออยู่ = ไม่มีใครอยู่หน้าเครื่อง งานที่ค้างในตะกร้าไม่มีทางถูกทำต่อ
  // ถือว่าว่าง เพื่อให้เครื่องที่ทิ้งไว้ข้ามคืนได้ bundle ใหม่ตอนเช้า
  const isLocked = computed(() => employeePinStore.active === true);

  // ลำดับสำคัญ: ของที่ "หายแล้วหายเลย" ต้องมาก่อนข้อยกเว้นเรื่องล็อคหน้าจอ
  // เครื่องที่ล็อคทิ้งไว้ทั้งที่ยังมีของค้างในตะกร้า = พนักงานเดินไปทำอย่างอื่นกลางบิล
  // ไม่ใช่เครื่องว่าง (ของเดิม ModalAppReload โหลดทับเลยในเคสนี้ ซึ่งกินบิลเขาไปฟรี ๆ)
  const isBusy = computed(() => {
    if (isOffline.value) return true;
    if (hasQueuedSync.value) return true;
    if (isOnPayment.value) return true;
    if (isOnCloseShop.value) return true;
    if (hasUnsavedCart.value) return true;
    if (isLocked.value) return false;
    if (isOnCartPage.value) return true;
    return false;
  });

  return {
    isBusy,
    hasUnsavedCart,
    isOnCartPage,
    isOnPayment,
    isOnCloseShop,
    isOffline,
    hasQueuedSync,
    isLocked,
  };
};

/**
 * เดิน localStorage รอบเดียวหาคิวที่ยังไม่ถูก release (ดู modules/pos/plugins/*.plugin.ts)
 *
 * ไม่เรียก useOffline().getItemsByType() ทีละชนิด เพราะแบบนั้นจะ JSON.parse
 * ทุกคีย์ในเครื่อง 5 รอบทุก 5 วินาที เครื่องที่มีใบเสร็จค้างเยอะจะกระตุก
 */
function hasQueuedSyncPayload(storeId: string): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      const raw = localStorage.getItem(key);
      // คัดด้วย string ก่อน parse — ของใน localStorage ส่วนใหญ่ไม่ใช่ payload คิว
      if (!raw || raw.charAt(0) !== "{" || !raw.includes('"type"')) continue;

      const payload = parseJson(raw);
      if (!payload || !SYNC_QUEUE_TYPES.has(payload.type)) continue;

      // ของค้างจากร้านอื่น (เครื่องเดียวกันเคย login คนละร้าน) ไม่ใช่งานของเราแล้ว
      // restore() ใน jobs.cloud.plugin ก็ข้ามมันเหมือนกัน แปลว่ามันจะค้างอยู่ตลอดไป
      // ถ้านับด้วยจะกลายเป็นเครื่องนั้นไม่มีวันได้อัปเดตเองเลย
      const payloadStoreId = payload?.data?.storeId;
      if (payloadStoreId && storeId && payloadStoreId !== storeId) continue;

      return true;
    }
  } catch {
    // อ่าน localStorage ไม่ได้ = ตอบไม่ได้ว่ามีงานค้างไหม
    // ตอบว่า "มี" ไว้ก่อน จะได้ไม่โหลดทับงานที่มองไม่เห็น
    return true;
  }

  return false;
}

/** คีย์เดียวพังไม่ควรทำให้ทั้งเครื่องถูกตัดสินว่า "มีงานค้าง" ตลอดกาล */
function parseJson(raw: string): any | null {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
